import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  TrashIcon,
  PencilSquareIcon,
  PlayIcon,
  PauseIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  TrophyIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ArchiveBoxIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import Card from '../UI/Card';
import Button from '../UI/Button';
import housePlayerService, {
  HousePlayer,
  HousePlayerSettings,
  HouseRoomOption,
} from '../../api/services/housePlayer.service';

/* =========================================================
   ROLE HELPERS
========================================================= */

function getAdminRole(): string {
  try {
    const raw = localStorage.getItem('admin_data');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return String(parsed?.role || '');
  } catch {
    return '';
  }
}

const CAN_WRITE_ROLES = new Set(['admin', 'super_admin']);

/* =========================================================
   SHARED STYLES
========================================================= */

const inputClass =
  'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';
const chipClass =
  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium';

/* =========================================================
   HELPERS
========================================================= */

function fmtEtb(v: number): string {
  const n = Number.isFinite(v) ? v : 0;
  return `ETB ${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function winRate(won: number, played: number): string {
  if (!played) return '—';
  return `${((won / played) * 100).toFixed(1)}%`;
}

function parseRoomLabel(
  label: string | null | undefined
): { roomId: string; betAmount: number } | null {
  if (!label) return null;
  const m = /^(Super\s+)?([0-9]+(?:\.[0-9]+)?)\s*ETB\s*—\s*(.+)$/.exec(
    label.trim()
  );
  if (!m) return null;
  return { roomId: m[3].trim(), betAmount: Number(m[2]) };
}

const REMOVE_SENTINEL = '__remove__';

/* =========================================================
   MAIN
========================================================= */

const HousePlayersTab: React.FC = () => {
  const qc = useQueryClient();
  const canWrite = CAN_WRITE_ROLES.has(getAdminRole());

  const settingsQuery = useQuery({
    queryKey: ['house-players', 'settings'],
    queryFn: () => housePlayerService.getSettings(),
  });

  const statsQuery = useQuery({
    queryKey: ['house-players', 'stats'],
    queryFn: () => housePlayerService.stats(),
  });

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [retiredFilter, setRetiredFilter] = useState<
    'all' | 'active' | 'retired'
  >('all');
  const [limit] = useState(20);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [searchDebounced, retiredFilter]);

  const listQuery = useQuery({
    queryKey: [
      'house-players',
      'list',
      page,
      limit,
      searchDebounced,
      retiredFilter,
    ],
    queryFn: () =>
      housePlayerService.list({
        page,
        limit,
        search: searchDebounced,
        retired: retiredFilter,
      }),
  });

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState<HousePlayer | null>(null);
  const [deleteOpen, setDeleteOpen] = useState<HousePlayer | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{
    type: 'success' | 'error';
    msg: string;
  } | null>(null);

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2600);
  };

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ['house-players'] });
  };

  const handleToggleRetired = async (p: HousePlayer) => {
    setBusy(true);
    try {
      await housePlayerService.toggleRetired(p.id);
      showToast('success', p.isRetired ? 'Restored' : 'Retired');
      refreshAll();
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || e?.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteOpen) return;
    setBusy(true);
    try {
      await housePlayerService.remove(deleteOpen.id);
      showToast('success', `Deleted ${deleteOpen.displayName}`);
      setDeleteOpen(null);
      refreshAll();
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || e?.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const players = listQuery.data?.players ?? [];
  const pagination = listQuery.data?.pagination;

  const pageTotals = useMemo(() => {
    let balance = 0;
    let played = 0;
    let won = 0;
    for (const p of players) {
      balance += p.balance || 0;
      played += p.gamesPlayed || 0;
      won += p.gamesWon || 0;
    }
    return { balance, played, won };
  }, [players]);

  return (
    <div className="space-y-5">
      <SettingsCard
        settings={settingsQuery.data}
        loading={settingsQuery.isLoading}
        canWrite={canWrite}
        onSaved={(msg) => {
          showToast('success', msg);
          refreshAll();
        }}
        onError={(msg) => showToast('error', msg)}
      />

      {statsQuery.data && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat
            label="Total"
            value={statsQuery.data.total.toLocaleString()}
            icon={<UserGroupIcon className="w-4 h-4" />}
          />
          <Stat
            label="Active"
            value={statsQuery.data.active.toLocaleString()}
            tone="green"
            icon={<CheckCircleIcon className="w-4 h-4" />}
          />
          <Stat
            label="Retired"
            value={statsQuery.data.retired.toLocaleString()}
            tone="gray"
            icon={<ArchiveBoxIcon className="w-4 h-4" />}
          />
          <Stat
            label="Games Played"
            value={statsQuery.data.totalGames.toLocaleString()}
            icon={<PlayIcon className="w-4 h-4" />}
          />
          <Stat
            label="Win Rate"
            value={`${(statsQuery.data.winRate * 100).toFixed(1)}%`}
            tone="green"
            icon={<TrophyIcon className="w-4 h-4" />}
          />
          <Stat
            label="Total Balance"
            value={fmtEtb(statsQuery.data.totalBalance)}
            tone="green"
            icon={<CurrencyDollarIcon className="w-4 h-4" />}
          />
        </div>
      )}

      <Card>
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name..."
              className={`${inputClass} pl-9`}
            />
          </div>

          <select
            value={retiredFilter}
            onChange={(e) => setRetiredFilter(e.target.value as any)}
            className={`${inputClass} md:w-40`}
          >
            <option value="all">All</option>
            <option value="active">Active only</option>
            <option value="retired">Retired only</option>
          </select>

          <button
            onClick={refreshAll}
            className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>

          {canWrite && (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <PlusIcon className="w-4 h-4 mr-1" />
              Add Players
            </Button>
          )}
        </div>
      </Card>

      <Card padding="sm" className="!p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-right">Balance</th>
                <th className="px-4 py-3 text-right">Played</th>
                <th className="px-4 py-3 text-right">Won</th>
                <th className="px-4 py-3 text-right">Win %</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {listQuery.isLoading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    Loading...
                  </td>
                </tr>
              ) : players.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    No house players found
                  </td>
                </tr>
              ) : (
                players.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                          {p.displayName
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .slice(0, 2)
                            .toUpperCase() || '?'}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {p.displayName}
                          </p>
                          <p className="text-[10px] text-gray-500 font-mono truncate max-w-[180px]">
                            {p.houseId}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-900 font-medium">
                      {fmtEtb(p.balance)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                      {p.gamesPlayed.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                      {p.gamesWon.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                      {winRate(p.gamesWon, p.gamesPlayed)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {p.isRetired ? (
                        <span
                          className={`${chipClass} bg-gray-100 text-gray-600`}
                        >
                          Retired
                        </span>
                      ) : (
                        <span
                          className={`${chipClass} bg-green-50 text-green-700`}
                        >
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {canWrite && (
                          <>
                            <button
                              onClick={() => setEditOpen(p)}
                              className="p-1.5 rounded hover:bg-gray-100"
                              title="Edit"
                            >
                              <PencilSquareIcon className="w-4 h-4 text-gray-500" />
                            </button>

                            <button
                              onClick={() => handleToggleRetired(p)}
                              disabled={busy}
                              className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-50"
                              title={p.isRetired ? 'Restore' : 'Retire'}
                            >
                              {p.isRetired ? (
                                <PlayIcon className="w-4 h-4 text-green-600" />
                              ) : (
                                <PauseIcon className="w-4 h-4 text-amber-600" />
                              )}
                            </button>

                            <button
                              onClick={() => setDeleteOpen(p)}
                              className="p-1.5 rounded hover:bg-red-50"
                              title="Delete"
                            >
                              <TrashIcon className="w-4 h-4 text-red-500" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {players.length > 0 && (
              <tfoot className="bg-gray-50/60 text-xs text-gray-600">
                <tr>
                  <td className="px-4 py-2 text-left font-medium">
                    Page total
                  </td>
                  <td className="px-4 py-2 text-right font-semibold tabular-nums">
                    {fmtEtb(pageTotals.balance)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {pageTotals.played.toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {pageTotals.won.toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {winRate(pageTotals.won, pageTotals.played)}
                  </td>
                  <td className="px-4 py-2" colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {pagination && pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Page {pagination.page} of {pagination.pages} ·{' '}
              {pagination.total} total
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 text-xs rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
              >
                Prev
              </button>
              <button
                disabled={page >= pagination.pages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-xs rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>

      {addOpen && (
        <AddDialog
          onClose={() => setAddOpen(false)}
          onDone={(msg, ok) => {
            showToast(ok ? 'success' : 'error', msg);
            if (ok) setAddOpen(false);
            refreshAll();
          }}
        />
      )}

      {editOpen && (
        <EditDialog
          player={editOpen}
          onClose={() => setEditOpen(null)}
          onDone={(msg, ok) => {
            showToast(ok ? 'success' : 'error', msg);
            if (ok) setEditOpen(null);
            refreshAll();
          }}
        />
      )}

      {deleteOpen && (
        <ConfirmDialog
          title={`Delete "${deleteOpen.displayName}"?`}
          description="This will remove the house player and its paired User row. This cannot be undone."
          confirmLabel={busy ? 'Deleting...' : 'Delete'}
          danger
          onCancel={() => setDeleteOpen(null)}
          onConfirm={handleDelete}
        />
      )}

      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
            toast.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

/* =========================================================
   SETTINGS CARD
   ---------------------------------------------------------
   Mobile fix:
   • Room cell  → basis-[55%] + min-w-0
   • Number input → basis-[40%] + min-w-0
   • gap-2 between
   55 + 40 + gap ≈ 100% — no overlap, no wrap, all screens.
   The `inputClass` (which hardcodes `w-full`) is NOT used on
   the <select> or the number input — explicit classes only.
========================================================= */

interface RoomRow {
  roomId: string;
  playersPerRoom: number;
}

interface SettingsCardProps {
  settings?: HousePlayerSettings;
  loading: boolean;
  canWrite: boolean;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
}

const SettingsCard: React.FC<SettingsCardProps> = ({
  settings,
  loading,
  canWrite,
  onSaved,
  onError,
}) => {
  const [form, setForm] = useState<HousePlayerSettings | undefined>(settings);
  const [rows, setRows] = useState<RoomRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const [roomOptions, setRoomOptions] = useState<HouseRoomOption[] | null>(
    null
  );
  const [loadingRooms, setLoadingRooms] = useState(false);
  const roomFetchStarted = useRef(false);

  const [pickerRow, setPickerRow] = useState<number>(-1);
  const [removeConfirmRow, setRemoveConfirmRow] = useState<number>(-1);

  const numberRefs = useRef<Array<HTMLInputElement | null>>([]);
  const pendingFocusIndex = useRef<number>(-1);

  useEffect(() => {
    setForm(settings);
    if (settings?.playersPerRoomByRoomId) {
      const entries = Object.entries(settings.playersPerRoomByRoomId).map(
        ([roomId, v]) => ({ roomId, playersPerRoom: Number(v) || 0 })
      );
      entries.sort((a, b) => a.roomId.localeCompare(b.roomId));
      setRows(entries);
    } else {
      setRows([]);
    }
    setDirty(false);
    setPickerRow(-1);
    setRemoveConfirmRow(-1);
  }, [settings]);

  useEffect(() => {
    const idx = pendingFocusIndex.current;
    if (idx < 0) return;
    const el = numberRefs.current[idx];
    if (!el) return;
    pendingFocusIndex.current = -1;
    requestAnimationFrame(() => {
      el.focus();
      const len = String(el.value ?? '').length;
      try {
        el.setSelectionRange(len, len);
      } catch {
        /* ignore */
      }
    });
  }, [rows.length]);

  const set = <K extends keyof HousePlayerSettings>(
    k: K,
    v: HousePlayerSettings[K]
  ) => {
    setForm((p) => (p ? { ...p, [k]: v } : p));
    setDirty(true);
  };

  const ensureRoomOptions = async () => {
    if (roomOptions || roomFetchStarted.current) return;
    roomFetchStarted.current = true;
    setLoadingRooms(true);
    try {
      const list = await housePlayerService.listRooms();
      setRoomOptions(list);
    } catch (e: any) {
      onError(
        e?.response?.data?.message || e?.message || 'Failed to load rooms'
      );
      roomFetchStarted.current = false;
    } finally {
      setLoadingRooms(false);
    }
  };

  const openPicker = (idx: number) => {
    setPickerRow(idx);
    setRemoveConfirmRow(-1);
    ensureRoomOptions();
  };

  const closePicker = () => setPickerRow(-1);

  const chooseRoom = (idx: number, value: string) => {
    if (value === REMOVE_SENTINEL) {
      setRemoveConfirmRow(idx);
      setPickerRow(-1);
      return;
    }
    setRows((prev) => {
      const next = prev.slice();
      next[idx] = { ...next[idx], roomId: value };
      return next;
    });
    setPickerRow(-1);
    setDirty(true);
  };

  const updateRowPlayers = (idx: number, value: number) => {
    setRows((prev) => {
      const next = prev.slice();
      next[idx] = { ...next[idx], playersPerRoom: value };
      return next;
    });
    setDirty(true);
  };

  const cancelRemoveRow = () => setRemoveConfirmRow(-1);

  const confirmRemoveRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    setRemoveConfirmRow(-1);
    if (pickerRow === idx) setPickerRow(-1);
    setDirty(true);
  };

  const addRow = () => {
    setRows((prev) => [...prev, { roomId: '', playersPerRoom: 10 }]);
    pendingFocusIndex.current = rows.length;
    setRemoveConfirmRow(-1);
    setDirty(true);
  };

  const roomInfoFor = (
    roomId: string
  ): { roomId: string; etb: number | null; missing: boolean } => {
    if (!roomId) return { roomId, etb: null, missing: false };
    if (roomOptions) {
      const opt = roomOptions.find((o) => o.roomId === roomId);
      if (opt) {
        const parsed = parseRoomLabel(opt.label);
        return { roomId, etb: parsed?.betAmount ?? null, missing: false };
      }
      return { roomId, etb: null, missing: true };
    }
    return { roomId, etb: null, missing: false };
  };

  const handleSave = async () => {
    if (!form) return;

    for (const r of rows) {
      if (!r.roomId.trim()) {
        onError('Every row needs a room');
        return;
      }
    }

    const seen = new Set<string>();
    for (const r of rows) {
      if (seen.has(r.roomId)) {
        onError(`Duplicate room: ${r.roomId}`);
        return;
      }
      seen.add(r.roomId);
    }

    setSaving(true);
    try {
      const playersPerRoomByRoomId: Record<string, number> = {};
      for (const r of rows) {
        const n = Number(r.playersPerRoom);
        if (!Number.isFinite(n) || n < 0) continue;
        playersPerRoomByRoomId[r.roomId] = Math.floor(n);
      }

      await housePlayerService.updateSettings({
        enabled: form.enabled,
        playersPerRoomByRoomId,
        applyToRoomIds: form.applyToRoomIds,
        applyToSuperBingo: form.applyToSuperBingo,
        fillDelayMs: form.fillDelayMs,
        maxHouseShareOfCartelas: form.maxHouseShareOfCartelas,
        skipWhenHumanSeatsAtLeast: form.skipWhenHumanSeatsAtLeast,
        startingBalance: form.startingBalance,
        autoTopUp: form.autoTopUp,
      });
      setDirty(false);
      onSaved('Settings saved');
    } catch (e: any) {
      onError(e?.response?.data?.message || e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <Card>
        <p className="text-sm text-gray-500">Loading settings...</p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">
            House Player Engine
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Configure how automated players join waiting rooms.
          </p>
        </div>

        <button
          onClick={() => canWrite && set('enabled', !form.enabled)}
          disabled={!canWrite}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            form.enabled ? 'bg-primary-500' : 'bg-gray-300'
          } ${!canWrite ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              form.enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 p-3 sm:p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-xs font-semibold text-gray-700">
              Players per room
            </h3>
            <p className="text-[10px] text-gray-500 mt-0.5">
              Click a room to pick from the server. To remove a room,
              pick “✕ Remove this room…” in its dropdown.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          {rows.length === 0 && (
            <p className="text-xs text-gray-400">No rooms configured.</p>
          )}

          {rows.map((r, idx) => {
            const picking = pickerRow === idx;
            const info = roomInfoFor(r.roomId);
            const confirming = removeConfirmRow === idx;

            return (
              <div
                key={`${r.roomId || 'new'}-${idx}`}
                className="rounded-lg border border-gray-200 bg-white p-2"
              >
                <div className="flex items-center gap-2 w-full">
                  {!picking ? (
                    <button
                      type="button"
                      onClick={() => canWrite && openPicker(idx)}
                      disabled={!canWrite}
                      className="basis-[55%] min-w-0 flex items-center justify-between gap-1 text-left border border-gray-200 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 hover:bg-gray-50 disabled:opacity-60"
                      title="Click to pick a room"
                    >
                      <div className="min-w-0 flex-1">
                        <p
                          className={`text-xs sm:text-sm truncate ${
                            r.roomId
                              ? 'font-medium text-gray-900'
                              : 'text-gray-400'
                          }`}
                        >
                          {r.roomId || 'Select room…'}
                        </p>
                        {r.roomId && (
                          <p
                            className={`text-[10px] truncate ${
                              info.missing
                                ? 'text-red-500'
                                : 'text-gray-500'
                            }`}
                          >
                            {info.missing
                              ? 'missing'
                              : info.etb != null
                              ? `${info.etb} ETB`
                              : '—'}
                          </p>
                        )}
                      </div>
                      <ChevronDownIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                    </button>
                  ) : (
                    <div className="basis-[55%] min-w-0 flex items-center gap-1">
                      <select
                        value={r.roomId}
                        disabled={loadingRooms || !canWrite}
                        onChange={(e) => chooseRoom(idx, e.target.value)}
                        className="flex-1 min-w-0 text-xs sm:text-sm border border-gray-200 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
                      >
                        <option value="">
                          {loadingRooms ? 'Loading rooms…' : 'Select room…'}
                        </option>
                        {roomOptions &&
                          roomOptions.map((opt) => (
                            <option key={opt.roomId} value={opt.roomId}>
                              {opt.label}
                            </option>
                          ))}
                        {r.roomId &&
                          roomOptions &&
                          !roomOptions.some((o) => o.roomId === r.roomId) && (
                            <option value={r.roomId}>
                              {r.roomId} (missing)
                            </option>
                          )}
                        <option value={REMOVE_SENTINEL}>
                          ✕ Remove this room…
                        </option>
                      </select>
                      <button
                        type="button"
                        onClick={closePicker}
                        className="p-1 rounded hover:bg-gray-100 flex-shrink-0"
                        title="Cancel"
                      >
                        <XMarkIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" />
                      </button>
                    </div>
                  )}

                  <input
                    ref={(el) => {
                      numberRefs.current[idx] = el;
                    }}
                    type="number"
                    min={0}
                    max={99999}
                    value={r.playersPerRoom}
                    disabled={!canWrite}
                    onChange={(e) =>
                      updateRowPlayers(idx, Number(e.target.value) || 0)
                    }
                    className="basis-[40%] min-w-0 text-xs sm:text-sm border border-gray-200 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-primary-400"
                    placeholder="players"
                  />

                  {canWrite && confirming && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => confirmRemoveRow(idx)}
                        className="text-[11px] font-medium text-red-600 hover:underline"
                      >
                        Yes
                      </button>
                      <span className="text-gray-300">·</span>
                      <button
                        type="button"
                        onClick={() => cancelRemoveRow()}
                        className="text-[11px] text-gray-500 hover:underline"
                      >
                        No
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {canWrite && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <Button size="sm" onClick={addRow}>
              <PlusIcon className="w-4 h-4 mr-1" />
              Add room
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>
            Room IDs (comma separated, empty = all)
          </label>
          <input
            value={(form.applyToRoomIds ?? []).join(', ')}
            disabled={!canWrite}
            onChange={(e) =>
              set(
                'applyToRoomIds',
                e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
              )
            }
            placeholder="room-10, room-30"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Max share of cartelas (0–1)</label>
          <input
            type="number"
            step="0.05"
            min={0}
            max={1}
            value={form.maxHouseShareOfCartelas}
            disabled={!canWrite}
            onChange={(e) =>
              set('maxHouseShareOfCartelas', Number(e.target.value) || 0)
            }
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Skip when humans ≥</label>
          <input
            type="number"
            min={0}
            value={form.skipWhenHumanSeatsAtLeast}
            disabled={!canWrite}
            onChange={(e) =>
              set('skipWhenHumanSeatsAtLeast', Number(e.target.value) || 0)
            }
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Starting balance (ETB)</label>
          <input
            type="number"
            min={0}
            value={form.startingBalance}
            disabled={!canWrite}
            onChange={(e) =>
              set('startingBalance', Number(e.target.value) || 0)
            }
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Apply to Super Bingo</label>
          <button
            onClick={() =>
              canWrite && set('applyToSuperBingo', !form.applyToSuperBingo)
            }
            disabled={!canWrite}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              form.applyToSuperBingo ? 'bg-primary-500' : 'bg-gray-300'
            } ${!canWrite ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                form.applyToSuperBingo ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {canWrite && (
        <div className="flex justify-end mt-4">
          <Button size="sm" disabled={!dirty || saving} onClick={handleSave}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      )}
    </Card>
  );
};
/* =========================================================
   STAT
========================================================= */

const Stat: React.FC<{
  label: string;
  value: string | number;
  tone?: 'green' | 'gray';
  icon?: React.ReactNode;
}> = ({ label, value, tone, icon }) => {
  const color =
    tone === 'green'
      ? 'text-green-600'
      : tone === 'gray'
      ? 'text-gray-500'
      : 'text-gray-900';
  const iconColor =
    tone === 'green'
      ? 'text-green-500'
      : tone === 'gray'
      ? 'text-gray-400'
      : 'text-primary-500';

  return (
    <Card>
      <div className="flex items-center gap-2 mb-1">
        {icon && <span className={iconColor}>{icon}</span>}
        <p className="text-[10px] uppercase text-gray-500 tracking-wide">
          {label}
        </p>
      </div>
      <p className={`text-lg font-semibold ${color}`}>{value}</p>
    </Card>
  );
};

/* =========================================================
   ADD DIALOG
========================================================= */

const AddDialog: React.FC<{
  onClose: () => void;
  onDone: (msg: string, ok: boolean) => void;
}> = ({ onClose, onDone }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const names = text
      .split(/\r?\n|,/)
      .map((s) => s.trim())
      .filter(Boolean);

    if (names.length === 0) {
      onDone('Add at least one name', false);
      return;
    }

    setBusy(true);
    try {
      const res = await housePlayerService.create({ names });
      onDone(
        `Created ${res.created} · Skipped ${res.skipped} · Failed ${res.failed}`,
        true
      );
    } catch (e: any) {
      onDone(e?.response?.data?.message || e?.message || 'Failed', false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Add House Players">
      <p className="text-xs text-gray-500 mb-2">
        One name per line, or comma separated. Duplicates are skipped.
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={8}
        placeholder={'Abebe\nAlmaz\nBekele'}
        className={inputClass}
      />
      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
        >
          Cancel
        </button>
        <Button size="sm" onClick={submit} disabled={busy}>
          {busy ? 'Creating...' : 'Create'}
        </Button>
      </div>
    </Modal>
  );
};

/* =========================================================
   EDIT DIALOG
========================================================= */

const EditDialog: React.FC<{
  player: HousePlayer;
  onClose: () => void;
  onDone: (msg: string, ok: boolean) => void;
}> = ({ player, onClose, onDone }) => {
  const [form, setForm] = useState({
    displayName: player.displayName,
    reactionBias: player.reactionBias,
    missBias: player.missBias,
    claimBias: player.claimBias,
    chatBias: player.chatBias,
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await housePlayerService.update(player.id, form);
      onDone('Saved', true);
    } catch (e: any) {
      onDone(e?.response?.data?.message || e?.message || 'Failed', false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title={`Edit ${player.displayName}`}>
      <div className="space-y-3">
        <div>
          <label className={labelClass}>Display name</label>
          <input
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            className={inputClass}
          />
        </div>

        {(
          [
            ['reactionBias', 'Reaction bias'],
            ['missBias', 'Miss bias'],
            ['claimBias', 'Claim bias'],
            ['chatBias', 'Chat bias'],
          ] as const
        ).map(([key, label]) => (
          <div key={key}>
            <label className={labelClass}>
              {label} <span className="text-gray-400">(0.5 – 1.5)</span>
            </label>
            <input
              type="number"
              step="0.05"
              min={0.5}
              max={1.5}
              value={(form as any)[key]}
              onChange={(e) =>
                setForm({ ...form, [key]: Number(e.target.value) || 1 })
              }
              className={inputClass}
            />
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
        >
          Cancel
        </button>
        <Button size="sm" onClick={submit} disabled={busy}>
          {busy ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </Modal>
  );
};

/* =========================================================
   CONFIRM DIALOG
========================================================= */

const ConfirmDialog: React.FC<{
  title: string;
  description: string;
  confirmLabel: string;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ title, description, confirmLabel, danger, onCancel, onConfirm }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-sm w-full p-5">
        <h3 className="text-base font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-4">{description}</p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm rounded-lg text-white ${
              danger
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-primary-500 hover:bg-primary-600'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* =========================================================
   MODAL
========================================================= */

const Modal: React.FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/50" onClick={onClose} />
    <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-gray-100"
        >
          <XMarkIcon className="w-4 h-4 text-gray-500" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

export default HousePlayersTab;